import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MainNavigation from '../components/MainNavigation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Enhanced Markdown renderer - 手機版優化
const MarkdownContent = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1 className="text-2xl md:text-3xl font-bold mt-6 md:mt-8 mb-3 md:mb-4 text-neutral-900 border-b pb-2 md:pb-3">{children}</h1>,
        h2: ({ children }) => <h2 className="text-xl md:text-2xl font-semibold mt-5 md:mt-6 mb-2 md:mb-3 text-neutral-900">{children}</h2>,
        h3: ({ children }) => <h3 className="text-lg md:text-xl font-semibold mt-4 mb-2 text-neutral-800">{children}</h3>,
        p: ({ children }) => <p className="mb-3 md:mb-4 leading-relaxed text-sm md:text-base text-neutral-700">{children}</p>,
        ul: ({ children }) => <ul className="list-disc list-inside mb-3 md:mb-4 space-y-1.5 md:space-y-2 text-sm md:text-base text-neutral-700">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside mb-3 md:mb-4 space-y-1.5 md:space-y-2 text-sm md:text-base text-neutral-700">{children}</ol>,
        li: ({ children }) => <li className="ml-3 md:ml-4">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-neutral-900">{children}</strong>,
        em: ({ children }) => <em className="italic text-neutral-600">{children}</em>,
        code: ({ inline, children }) => 
          inline 
            ? <code className="bg-neutral-100 px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs md:text-sm font-mono text-obsidian-900 border border-neutral-200">{children}</code>
            : <code className="block bg-neutral-50 p-3 md:p-4 rounded-lg text-xs md:text-sm font-mono overflow-x-auto mb-3 md:mb-4 border border-neutral-200 text-neutral-700">{children}</code>,
        pre: ({ children }) => <pre className="bg-neutral-50 p-3 md:p-4 rounded-lg overflow-x-auto mb-3 md:mb-4 border border-neutral-200">{children}</pre>,
        a: ({ href, children }) => <a href={href} className="text-obsidian-900 underline underline-offset-2 decoration-obsidian-400 hover:decoration-obsidian-900 break-words transition-colors" target="_blank" rel="noopener noreferrer">{children}</a>,
        blockquote: ({ children }) => <blockquote className="border-l-4 border-obsidian-800 pl-3 md:pl-4 italic my-3 md:my-4 text-sm md:text-base text-neutral-600">{children}</blockquote>,
        table: ({ children }) => (
          <div className="overflow-x-auto my-3 md:my-4 rounded-lg border border-neutral-200">
            <table className="min-w-full border-collapse text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-neutral-50">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-neutral-200 last:border-0">{children}</tr>,
        th: ({ children }) => <th className="px-3 md:px-4 py-2 text-left text-xs font-semibold text-neutral-700 whitespace-nowrap">{children}</th>,
        td: ({ children }) => <td className="px-3 md:px-4 py-2 text-sm text-neutral-600">{children}</td>,
        hr: () => <hr className="my-4 md:my-6 border-neutral-200" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const CaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCase();
  }, [caseId]);

  const fetchCase = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/case-library/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      console.error('獲取案例失敗:', error);
      toast.error('獲取案例失敗');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-atelier="true" className="flex h-screen bg-cream-150">
        <MainNavigation />
        <div data-theme="cream" className="flex-1 flex items-center justify-center surface-cream">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-cream-300 border-t-cream-900 mx-auto mb-3"></div>
            <p className="label-micro text-cream-500">LOADING</p>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <div data-atelier="true" className="flex flex-col md:flex-row h-screen bg-cream-150 pb-14 md:pb-0">
      <MainNavigation />

      <div
        data-theme="cream"
        className="flex-1 flex flex-col overflow-hidden surface-cream"
        data-testid="case-detail-page"
      >
        <div className="h-[1px] bg-gradient-to-r from-transparent via-lime-500/40 to-transparent flex-shrink-0" />

        <header className="surface-glass-cream border-b border-cream-300 px-4 md:px-12 py-4 flex-shrink-0 relative z-10">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/cases')}
              className="rounded-md text-cream-500 hover:text-cream-900 hover:bg-cream-200 h-8 px-2"
            >
              <ArrowLeft className="w-4 h-4 md:mr-1.5" strokeWidth={1.5} />
              <span className="hidden md:inline text-sm">返回</span>
            </Button>
            <span className="h-4 w-px bg-cream-300" />
            <span className="label-micro text-cream-500">CASE · DETAIL</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 md:px-12 py-10 md:py-16 space-y-10">
            <header className="space-y-5">
              <div className="flex flex-wrap gap-2">
                {caseData.industry_tags?.map((tag, i) => (
                  <span key={`ind-${i}`} className="label-micro text-cream-500">
                    {tag}
                  </span>
                ))}
                {caseData.video_type_tags?.map((tag, i) => (
                  <React.Fragment key={`vt-${i}`}>
                    <span className="text-cream-400">·</span>
                    <span className="label-micro text-cream-500">{tag}</span>
                  </React.Fragment>
                ))}
                {caseData.platform_tags?.map((tag, i) => (
                  <React.Fragment key={`pt-${i}`}>
                    <span className="text-cream-400">·</span>
                    <span className="label-micro text-cream-900 bg-cream-200 px-2 py-0.5 rounded-full">{tag}</span>
                  </React.Fragment>
                ))}
              </div>

              <h1 className="font-serif font-semibold text-cream-900 tracking-tighter leading-[1.1] text-3xl md:text-5xl lg:text-6xl">
                {caseData.title}
              </h1>
              {caseData.description && (
                <p className="text-cream-700 text-base md:text-lg leading-relaxed max-w-2xl">
                  {caseData.description}
                </p>
              )}
            </header>

            <div className="rounded-2xl overflow-hidden bg-obsidian-900 border border-cream-300">
              <div className="md:aspect-video" style={{ aspectRatio: window.innerWidth < 768 ? '9/16' : '16/9' }}>
                <iframe
                  src={`${caseData.vimeo_embed_url}?title=0&byline=0&portrait=0`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={caseData.title}
                />
              </div>
            </div>

            {caseData.original_video_url && (
              <a
                href={caseData.original_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-cream-900 hover:text-cream-700 transition-colors label-micro border-b border-cream-400 pb-1"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                VIEW · ORIGINAL
              </a>
            )}

            <article className="pt-8 border-t border-cream-300">
              <div className="label-micro text-cream-500 mb-3 flex items-center gap-2">
                <span className="h-px w-6 bg-cream-400" />
                ANALYSIS
              </div>
              <h2 className="font-serif font-semibold text-cream-900 text-2xl md:text-3xl tracking-tight mb-6">
                影片拆解分析
              </h2>
              <div className="prose prose-neutral max-w-none">
                <MarkdownContent content={caseData.analysis_content} />
              </div>
            </article>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CaseDetail;
